import React, { useState } from "react";
import { Copy, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  isValidPhoneNumber,
  formatPhoneNumberIntl,
  parsePhoneNumber,
} from "react-phone-number-input";
import { countryFlagEmoji } from "@/optimise/PhoneCountryFields";

/**
 * PhoneDisplay — canonical read-only telephone renderer.
 *
 * Renders an E.164 string like `"+441616677640"` as:
 *
 *     🇬🇧  +44 161 667 7640    [tel:] [copy]
 *
 * with:
 *  - Country flag derived from the E.164 prefix
 *  - International formatting via `react-phone-number-input`
 *  - `tel:` click-to-dial link
 *  - Copy-to-clipboard button (single canonical implementation)
 *
 * Falls back to a soft "—" placeholder when the value is empty or invalid.
 * Used everywhere OrangeBySite renders a stored phone number outside a
 * form (Client Profile, Client Cards, Agreements, Invoices, etc.).
 */
export default function PhoneDisplay({
  value,
  testId = "phone-display",
  showCopy = true,
  showDial = true,
  compact = false,
  className = "",
}) {
  const [copied, setCopied] = useState(false);
  if (!value || typeof value !== "string" || !value.trim()) {
    return (
      <span className="text-[#8A8A8A]" data-testid={`${testId}-empty`}>—</span>
    );
  }

  let parsed = null;
  try { parsed = parsePhoneNumber(value); } catch (_) { /* invalid */ }
  const valid  = !!parsed && isValidPhoneNumber(value);
  const flag   = parsed?.country ? countryFlagEmoji(parsed.country) : "";
  const pretty = valid ? formatPhoneNumberIntl(value) : value;

  // Phase 3I.4P — compact variant: flag + formatted number on one line.
  // No copy button, no "check format" badge — used by the HQ Clients
  // table where the profile-level PhoneDisplay already exposes those
  // affordances.
  if (compact) {
    return (
      <span
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`}
      >
        {flag && (
          <span aria-hidden className="text-base leading-none"
                data-testid={`${testId}-flag`}>{flag}</span>
        )}
        <span className="font-medium text-[#0A0A0A]"
              data-testid={`${testId}-text`}>{pretty}</span>
      </span>
    );
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Phone copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error("Couldn't copy phone");
    }
  };

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-2 flex-wrap ${className}`}
    >
      {flag && (
        <span aria-hidden className="text-base leading-none"
              data-testid={`${testId}-flag`}>{flag}</span>
      )}
      {showDial ? (
        <a
          href={`tel:${value}`}
          className="font-medium hover:underline text-[#0A0A0A]"
          data-testid={`${testId}-tel`}
        >
          {pretty}
        </a>
      ) : (
        <span className="font-medium" data-testid={`${testId}-text`}>{pretty}</span>
      )}
      {showCopy && (
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copy phone number"
          title="Copy phone number"
          data-testid={`${testId}-copy`}
          className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-[#EAEAEA] hover:border-[#FF6A00] hover:bg-[#FFF3E8] transition-colors"
        >
          {copied
            ? <Check size={12} className="text-[#0FA958]"/>
            : <Copy  size={12} className="text-[#4A4A4A]"/>}
        </button>
      )}
      {!valid && (
        <span
          className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#C1350B]/70"
          title="This number is not a valid international format"
          data-testid={`${testId}-invalid-badge`}
        >
          check format
        </span>
      )}
    </span>
  );
}
